import type { Probe, Finding } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/**
 * Locale and timezone.
 *
 * The interesting check here is not the timezone value itself but whether it is
 * *coherent*: an anti-detect tool that spoofs the timezone string often leaves
 * getTimezoneOffset() frozen or inconsistent, because patching the IANA name is
 * easy but patching the engine's offset calculation is not.
 */
export const localeProbe: Probe = {
  id: 'locale',
  label: 'Locale & timezone',
  layer: 'locale',
  tier: 'client',
  category: 'browser',
  description: 'Languages, Intl-resolved locale, and timezone coherence',
  budgetMs: 1000,

  async run() {
    const n = navigator;
    const dtf = new Intl.DateTimeFormat();
    const resolved = dtf.resolvedOptions();
    const timezone = resolved.timeZone ?? null;
    const languages = n.languages ? Array.from(n.languages) : null;

    // Sample the current offset and that of two known instants; a genuine
    // engine applies DST between them where the zone observes it.
    const now = new Date();
    const january = new Date(now.getFullYear(), 0, 1).getTimezoneOffset();
    const july = new Date(now.getFullYear(), 6, 1).getTimezoneOffset();
    const current = now.getTimezoneOffset();
    const observesDst = january !== july;

    const findings: Finding[] = [];

    // Coherence: navigator.language primary should prefix the resolved locale
    // in most configurations (a mismatch is possible with valid setups, so
    // confidence is medium and the contribution is small).
    const primaryLang = n.language;
    const primaryIsEn = primaryLang?.toLowerCase().startsWith('en');

    return {
      signals: {
        language: ok(primaryLang ?? null),
        languages: ok(languages),
        resolvedLocale: ok(resolved.locale ?? null),
        calendar: ok(resolved.calendar ?? null),
        numberingSystem: ok(resolved.numberingSystem ?? null),
        timeZone: ok(timezone),
        // Live offset, not a constant: reading it fresh each run means a
        // spoof that only patches the name is caught by the offset disagreeing.
        getTimezoneOffsetMinutes: ok(current, 'client'),
        januaryOffsetMinutes: ok(january, 'client'),
        julyOffsetMinutes: ok(july, 'client'),
        observesDst: ok(observesDst, 'derived'),
        primaryIsEnglish: ok(primaryIsEn, 'derived'),
      },
      findings,
      parts: {
        localeTimezone: {
          // Hash the zone name and whether it observes DST, not the live offset
          // (which legitimately changes twice a year).
          timeZone: timezone,
          language: primaryLang ?? null,
          languages: languages ?? [],
          observesDst,
        },
      },
    };
  },
};

registerProbe(localeProbe);
